import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt, faSpinner, faPlus, faFileAlt, faTimes
} from '@fortawesome/free-solid-svg-icons';
import { disputesAPI } from '../services/api';
import './Disputes.css';
import { useNotifications } from '../context/NotificationContext';

const Disputes = () => {
  const { showCustomToast } = useNotifications();
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    orderId: '',
    reason: '',
    description: ''
  });

  const reasons = {
    'not_received': 'Item Not Received',
    'damaged': 'Item Damaged',
    'not_as_described': 'Not As Described',
    'counterfeit': 'Counterfeit Item',
    'wrong_item': 'Wrong Item',
    'refund_issue': 'Refund Issue',
    'other': 'Other'
  };

  useEffect(() => {
    fetchDisputes();
  }, []);

  const fetchDisputes = async () => {
    try {
      setLoading(true);
      const response = await disputesAPI.getDisputes();
      setDisputes(response?.data?.disputes || []);
    } catch (error) {
      setDisputes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await disputesAPI.createDispute(formData);
      setShowCreateForm(false);
      setFormData({ orderId: '', reason: '', description: '' });
      fetchDisputes();
    } catch (error) {
      showCustomToast({ type: 'error', message: 'Failed to create dispute' });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: '#FF6B6B',
      under_review: '#4ECDC4',
      resolved: '#45B7D1',
      closed: '#96CEB4'
    };
    return colors[status] || '#9CA3AF';
  };

  if (loading) {
    return (
      <div className="AutoboyExpress-disputes-loading">
        <FontAwesomeIcon icon={faSpinner} spin size="3x" />
      </div>
    );
  }

  return (
    <div className="AutoboyExpress-disputes-container">
      <div className="AutoboyExpress-disputes-header">
        <div>
          <h1>My Disputes</h1>
          <p>Manage your order disputes and resolutions</p>
        </div>
        <button
          className="AutoboyExpress-disputes-create-btn"
          onClick={() => setShowCreateForm(true)}
        >
          <FontAwesomeIcon icon={faPlus} />
          File Dispute
        </button>
      </div>

      {showCreateForm && (
        <div className="AutoboyExpress-disputes-modal" onClick={() => setShowCreateForm(false)}>
          <div className="AutoboyExpress-disputes-form" onClick={(e) => e.stopPropagation()}>
            <div className="AutoboyExpress-disputes-form-header">
              <h2>File a Dispute</h2>
              <button onClick={() => setShowCreateForm(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="AutoboyExpress-form-group">
                <label>Order ID *</label>
                <input
                  type="text"
                  value={formData.orderId}
                  onChange={(e) => setFormData({ ...formData, orderId: e.target.value })}
                  required
                />
              </div>
              <div className="AutoboyExpress-form-group">
                <label>Reason *</label>
                <select
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  required
                >
                  <option value="">Select a reason</option>
                  {Object.entries(reasons).map(([key, value]) => (
                    <option key={key} value={key}>{value}</option>
                  ))}
                </select>
              </div>
              <div className="AutoboyExpress-form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={6}
                  required
                />
              </div>
              <div className="AutoboyExpress-form-actions">
                <button type="button" onClick={() => setShowCreateForm(false)}>
                  Cancel
                </button>
                <button type="submit">Submit Dispute</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="AutoboyExpress-disputes-list">
        {disputes.length === 0 ? (
          <div className="AutoboyExpress-disputes-empty">
            <FontAwesomeIcon icon={faShieldAlt} size="4x" />
            <h3>No Disputes</h3>
            <p>You haven't raised any disputes yet</p>
          </div>
        ) : (
          disputes.map((dispute) => (
            <div key={dispute.id || dispute._id} className="AutoboyExpress-dispute-card">
              <div className="AutoboyExpress-dispute-header">
                <div>
                  <h3>Order #{dispute.order_id || dispute.orderId}</h3>
                  <p>{reasons[dispute.reason] || dispute.reason}</p>
                </div>
                <span
                  className="AutoboyExpress-dispute-status"
                  style={{ backgroundColor: getStatusColor(dispute.status) }}
                >
                  {dispute.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <p className="AutoboyExpress-dispute-description">{dispute.description}</p>
              <div className="AutoboyExpress-dispute-footer">
                {dispute.disputed_amount && (
                  <span className="AutoboyExpress-dispute-amount">
                    ₦{dispute.disputed_amount.toLocaleString()}
                  </span>
                )}
                <span className="AutoboyExpress-dispute-date">
                  {new Date(dispute.created_at || dispute.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Disputes;
